import React from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import { TooltipButton } from "components/common/TooltipButton";
import { useEscapeStack } from "components/hooks/useEscapeStack";

export type DialogSize = "default" | "wide";

interface DialogProps {
  open: boolean;
  title?: string | React.ReactNode;
  ariaLabel?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  autoSpacing?: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  size?: DialogSize;
  titleDivider?: boolean;
  blurBackdrop?: boolean;
  backdropStyle?: React.CSSProperties;
}

export const Dialog: React.FC<DialogProps> = ({
  open,
  title,
  ariaLabel,
  children,
  actions,
  className = "",
  style,
  autoSpacing = true,
  onClose,
  showCloseButton = true,
  size = "default",
  titleDivider = true,
  blurBackdrop = false,
  backdropStyle,
}) => {
  useEscapeStack(open, onClose);

  if (!open) return null;

  return createPortal(
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black/20 z-50 ${
        blurBackdrop ? "backdrop-blur-sm" : ""
      }`}
      style={backdropStyle}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={
          ariaLabel ?? (typeof title === "string" ? title : undefined)
        }
        className={`relative bg-bg-subtle border border-border rounded-app shadow-lg select-none cursor-default ${size === "wide" ? "w-215 max-w-[95vw] max-h-[80vh] flex flex-col" : "w-105 max-h-[95vh] flex flex-col overflow-hidden p-6"} ${className}`}
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--font-size)",
          ...style,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <div
            className={`flex items-center justify-between ${
              size === "wide"
                ? `px-5 py-3 ${titleDivider ? "border-b border-border" : ""}`
                : "mb-4"
            }`}
          >
            {typeof title === "string" ? (
              <p className="text-fg-bold">{title}</p>
            ) : (
              <div className="min-w-0 flex-1">{title}</div>
            )}
            {onClose && showCloseButton && (
              <TooltipButton
                title="Close"
                variant="default"
                size="xs"
                onClick={onClose}
              >
                <CloseIcon />
              </TooltipButton>
            )}
          </div>
        ) : null}
        {autoSpacing ? (
          <div className="space-y-4 flex-1 min-h-0 overflow-auto">
            {children}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">{children}</div>
        )}
        {actions ? (
          <div
            className={`flex shrink-0 items-center justify-end space-x-2 ${
              size === "wide" ? "px-5 py-3 border-t border-border" : "mt-6"
            }`}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};
